import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { procedureStatusColorsQuery, ProcedureStatusColor } from '@/graph/procedureStatusColors';
import { getLegacyAnesthesiologistColor, getLegacyPanelColor, getLegacyScheduleColor } from '@/util/legacyStatusColors';
import { normalizeStatus, NormalizedStatus } from '@/components/pages/kiosk/schedule/shared/procedureUtils';
import './statusContour.css';

export type StatusColorVariant = 'schedule' | 'anesthesiologist' | 'panel';
type StatusColorMap = Record<string, ProcedureStatusColor>;
const noExclusions: ReadonlySet<string> = new Set();
const flashStyle = { animation: 'procedureStatusContourFlash 1.2s ease-in-out infinite' };
type StatusRoom = { type?: string | null } | null;

// Flags use schedule step names; canonical patient-status names resolve through the existing normalizer.
// Ready is already the schedule name for ReadyForSurgery; a patient's raw Ready status normalizes to OR.
const configFlagStatus = (status: string) => {
  if (status === NormalizedStatus.Ready) return status;
  const normalized = normalizeStatus(status);
  return normalized === NormalizedStatus.Unknown ? status : normalized;
};

/** Existing callers that only need a color keep the same interface. */
export function useStatusColor(variant: StatusColorVariant) {
  return useStatusDisplay(variant).getColor;
}

/** One query, with exact color lookup and normalized step matching for the new flags. */
export function useStatusDisplay(variant: StatusColorVariant = 'schedule', options?: { skip?: boolean }) {
  const { data } = useQuery<{ procedureStatusColors: ProcedureStatusColor[] }>(procedureStatusColorsQuery, options);
  const { backendMap, flashingColors, excludedFromSchedule, excludedFromMonitor } = useMemo(() => {
    const rows = data?.procedureStatusColors;
    if (!Array.isArray(rows) || rows.length === 0) {
      return {
        backendMap: null,
        flashingColors: {},
        excludedFromSchedule: noExclusions,
        excludedFromMonitor: noExclusions,
      };
    }
    const backendMap: StatusColorMap = {};
    const flashingColors: Record<string, string> = {};
    const excludedFromSchedule = new Set<string>();
    const excludedFromMonitor = new Set<string>();
    for (const row of rows) {
      if (row?.status && row?.color) backendMap[row.status] = row;
      if (!row?.status) continue;
      const step = configFlagStatus(row.status);
      if (row.flash && (row.status === step || !flashingColors[step])) flashingColors[step] = row.color;
      if (row.excludeFromSchedule) excludedFromSchedule.add(step);
      if (row.excludeFromMonitor) excludedFromMonitor.add(step);
    }
    return { backendMap, flashingColors, excludedFromSchedule, excludedFromMonitor };
  }, [data?.procedureStatusColors]);

  const getColor = useCallback(
    (status: string, hospitalId?: number, defaultColor?: string | null) => {
      if (backendMap) {
        if (backendMap[status] != null) return backendMap[status].color;
        if (variant === 'panel') return defaultColor === undefined ? null : defaultColor;
        return defaultColor === undefined ? '#00A7F7' : defaultColor;
      }
      switch (variant) {
        case 'anesthesiologist':
          return getLegacyAnesthesiologistColor(status, hospitalId, defaultColor);
        case 'panel':
          return getLegacyPanelColor(status, hospitalId);
        case 'schedule':
        default:
          return getLegacyScheduleColor(status, hospitalId, defaultColor);
      }
    },
    [backendMap, variant]
  );
  const getFlashingContourColor = useCallback(
    (status?: string | null, room?: StatusRoom, isDpuAsPreOp = false) => {
      if (!status) return undefined;
      const normalized = variant === 'panel' ? normalizeStatus(status, room, isDpuAsPreOp) : status;
      return flashingColors[normalized === NormalizedStatus.Unknown ? status : normalized];
    },
    [flashingColors, variant]
  );
  const getContourStyle = useCallback(
    (status?: string | null, room?: StatusRoom, isDpuAsPreOp = false) =>
      getFlashingContourColor(status, room, isDpuAsPreOp) ? flashStyle : undefined,
    [getFlashingContourColor]
  );
  return { getColor, getContourStyle, getFlashingContourColor, excludedFromSchedule, excludedFromMonitor };
}
